import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { places } from "@/mocks/places";

const PLACES_PER_PAGE = 12;

const regions = ["Tümü", "İç Anadolu", "Güneydoğu Anadolu", "Batı Karadeniz", "Doğu Karadeniz", "Ege", "Akdeniz", "Doğu Anadolu", "Trakya", "Marmara"];

// Collect all unique tags from places data
const allTags = ["Tümü", ...Array.from(new Set(places.flatMap((p) => p.tags)))];

const placeCoords: Record<string, { lat: number; lng: number; zoom: number }> = {
  "kapadokya":        { lat: 38.6431, lng: 34.8289, zoom: 12 },
  "pamukkale":        { lat: 37.9200, lng: 29.1200, zoom: 13 },
  "efes":             { lat: 37.9395, lng: 27.3417, zoom: 14 },
  "nemrut-dagi":      { lat: 37.9811, lng: 38.7411, zoom: 13 },
  "gobeklitepe":      { lat: 37.2231, lng: 38.9225, zoom: 14 },
  "ani-harabeleri":   { lat: 40.5058, lng: 43.5722, zoom: 13 },
  "bodrum":           { lat: 37.0344, lng: 27.4305, zoom: 13 },
  "olüdeniz":         { lat: 36.5500, lng: 29.1167, zoom: 13 },
  "mardin":           { lat: 37.3212, lng: 40.7245, zoom: 13 },
  "safranbolu":       { lat: 41.2500, lng: 32.6833, zoom: 14 },
  "ayder-yaylasi":    { lat: 40.9333, lng: 41.1167, zoom: 13 },
  "halfeti":          { lat: 37.2667, lng: 37.8667, zoom: 13 },
  "sirince":          { lat: 37.9333, lng: 27.4167, zoom: 14 },
  "sumela-manastiri": { lat: 40.6917, lng: 39.6583, zoom: 14 },
  "konya-mevlana":    { lat: 37.8667, lng: 32.4833, zoom: 13 },
  "alacati":          { lat: 38.2667, lng: 26.3833, zoom: 14 },
  "edirne-selimiye":  { lat: 41.6781, lng: 26.5558, zoom: 14 },
  "trabzon-uzungol":  { lat: 40.6167, lng: 40.2833, zoom: 13 },
  "van-akdamar":      { lat: 38.3500, lng: 43.0500, zoom: 11 },
  "ishak-pasa-sarayi":{ lat: 39.5500, lng: 44.0833, zoom: 14 },
  "diyarbakir-surlar":{ lat: 37.9167, lng: 40.2333, zoom: 13 },
  "antalya-kaleiçi":  { lat: 36.8841, lng: 30.7056, zoom: 14 },
};

type ViewMode = "grid" | "map";

// Normalize tag for URL comparison (lowercase, Turkish chars)
const normalizeTag = (tag: string) => tag.toLowerCase().replace(/ı/g, "i").replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ş/g, "s").replace(/ö/g, "o").replace(/ç/g, "c");

const PlacesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const catParam = searchParams.get("cat") || "";
  const initialTag = catParam
    ? allTags.find((t) => normalizeTag(t) === normalizeTag(catParam)) || "Tümü"
    : "Tümü";

  const [selectedRegion, setSelectedRegion] = useState("Tümü");
  const [selectedTag, setSelectedTag] = useState(initialTag);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [mapPlace, setMapPlace] = useState(places[0]);

  // Sync selectedTag when URL cat param changes (e.g. browser back/forward)
  useEffect(() => {
    const cat = searchParams.get("cat") || "";
    const matched = cat
      ? allTags.find((t) => normalizeTag(t) === normalizeTag(cat)) || "Tümü"
      : "Tümü";
    setSelectedTag(matched);
  }, [searchParams]);

  const currentPage = Math.max(1, parseInt(searchParams.get("page") || "1", 10));

  const setPage = useCallback((page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page === 1) { params.delete("page"); } else { params.set("page", String(page)); }
    setSearchParams(params, { replace: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchParams, setSearchParams]);

  const handleTagSelect = useCallback((tag: string) => {
    setSelectedTag(tag);
    const params = new URLSearchParams(searchParams);
    if (tag === "Tümü") {
      params.delete("cat");
    } else {
      params.set("cat", normalizeTag(tag));
    }
    params.delete("page");
    setSearchParams(params, { replace: false });
  }, [searchParams, setSearchParams]);

  const filtered = useMemo(() => {
    return places.filter((p) => {
      const matchRegion = selectedRegion === "Tümü" || p.region === selectedRegion;
      const matchTag = selectedTag === "Tümü" || p.tags.some((t) => normalizeTag(t) === normalizeTag(selectedTag));
      const matchSearch =
        !searchQuery ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.shortDesc.toLowerCase().includes(searchQuery.toLowerCase());
      return matchRegion && matchTag && matchSearch;
    });
  }, [selectedRegion, selectedTag, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PLACES_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedPlaces = filtered.slice((safePage - 1) * PLACES_PER_PAGE, safePage * PLACES_PER_PAGE);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, selectedTag, searchQuery]);

  // SEO
  useEffect(() => {
    const pageLabel = safePage > 1 ? ` — Sayfa ${safePage}` : "";
    const catLabel = selectedTag !== "Tümü" ? ` — ${selectedTag}` : "";
    const title = `Türkiye'de Gezilecek Yerler${catLabel} — Kapadokya, Efes, Pamukkale ve Daha Fazlası${pageLabel}`;
    const desc = "Türkiye'nin en güzel destinasyonları: Kapadokya peri bacaları, Efes antik kenti, Pamukkale travertenleri, Nemrut Dağı ve daha fazlası. Bölge ve kategoriye göre filtrele.";
    const canonicalBase = "https://geziturkiyem.com/places";
    const canonicalUrl = safePage > 1 ? `${canonicalBase}?page=${safePage}` : canonicalBase;

    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setCanonical = (url: string) => {
      let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
      el.setAttribute("href", url);
    };
    const setLinkRel = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const removeLinkRel = (rel: string) => { document.querySelector(`link[rel="${rel}"]`)?.remove(); };
    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) { el = document.createElement("script"); el.id = id; el.setAttribute("type", "application/ld+json"); document.head.appendChild(el); }
      el.textContent = JSON.stringify(data);
    };

    setCanonical(canonicalUrl);
    if (safePage > 1) {
      setLinkRel("prev", safePage === 2 ? canonicalBase : `${canonicalBase}?page=${safePage - 1}`);
    } else { removeLinkRel("prev"); }
    if (safePage < totalPages) {
      setLinkRel("next", `${canonicalBase}?page=${safePage + 1}`);
    } else { removeLinkRel("next"); }

    setMeta("description", desc);
    setMeta("keywords", "türkiye gezilecek yerler, kapadokya, efes antik kenti, pamukkale, türkiye destinasyonları, seyahat rehberi, nemrut dağı, mardin, karadeniz yaylaları");
    setMeta("og:title", title, true);
    setMeta("og:description", desc, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", canonicalUrl, true);
    setMeta("og:site_name", "Yerel Bilgi — Türkiye Seyahat Rehberi", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:site", "@geziturkiyem");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);

    injectSchema("schema-places-page", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": title,
      "description": desc,
      "url": canonicalUrl,
      "inLanguage": "tr-TR",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://geziturkiyem.com/" },
          { "@type": "ListItem", "position": 2, "name": "Gezilecek Yerler", "item": canonicalBase },
          ...(safePage > 1 ? [{ "@type": "ListItem", "position": 3, "name": `Sayfa ${safePage}`, "item": canonicalUrl }] : []),
        ],
      },
    });

    return () => { document.title = "Türkiye Seyahat Rehberi"; };
  }, [safePage, totalPages, selectedTag]);

  const getMapUrl = (place: typeof places[0]) => {
    const coords = placeCoords[place.id];
    if (coords) {
      return `https://www.google.com/maps/embed/v1/place?key=AIzaSyD-9tSrke72PouQMnMX-a7eZSW0jkFMBWY&q=${encodeURIComponent(place.name + " " + place.city + " Turkey")}&center=${coords.lat},${coords.lng}&zoom=${coords.zoom}&language=tr`;
    }
    return `https://www.google.com/maps/embed/v1/place?key=AIzaSyD-9tSrke72PouQMnMX-a7eZSW0jkFMBWY&q=${encodeURIComponent(place.name + " " + place.city + " Turkey")}&language=tr`;
  };

  const PaginationBar = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="mt-12 flex items-center justify-center gap-2 flex-wrap">
        <button
          onClick={() => setPage(safePage - 1)}
          disabled={safePage === 1}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
            safePage === 1 ? "bg-stone-100 text-stone-300 cursor-not-allowed" : "bg-stone-100 text-stone-600 hover:bg-stone-200 cursor-pointer"
          }`}
        >
          <i className="ri-arrow-left-s-line"></i> Önceki
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
          const isActive = p === safePage;
          const isNear = Math.abs(p - safePage) <= 2 || p === 1 || p === totalPages;
          if (!isNear) {
            if (p === safePage - 3 || p === safePage + 3) return <span key={p} className="text-stone-300 text-sm px-1">…</span>;
            return null;
          }
          return (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-semibold transition-colors cursor-pointer ${
                isActive ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-amber-100 hover:text-amber-700"
              }`}
            >
              {p}
            </button>
          );
        })}
        <button
          onClick={() => setPage(safePage + 1)}
          disabled={safePage === totalPages}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
            safePage === totalPages ? "bg-stone-100 text-stone-300 cursor-not-allowed" : "bg-stone-100 text-stone-600 hover:bg-stone-200 cursor-pointer"
          }`}
        >
          Sonraki <i className="ri-arrow-right-s-line"></i>
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-16 bg-stone-900 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Turkey%20travel%20destinations%20collage%20mountains%20sea%20historical%20places%20aerial%20view%20beautiful%20landscape&width=1400&height=400&seq=places-header&orientation=landscape"
          alt="Gezilecek Yerler"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-30"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">Keşfet</span>
          <h1
            className="text-4xl md:text-5xl font-bold text-white mt-2 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Gezilecek Yerler
          </h1>
          <p className="text-stone-300 text-base max-w-xl mx-auto">
            Türkiye'nin dört bir yanından, turistlerin henüz keşfetmediği muhteşem destinasyonlar.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 z-30 bg-white border-b border-stone-100 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-3">
          {/* Row 1: Search + View Toggle */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="flex items-center bg-stone-50 border border-stone-200 rounded-full px-4 py-2 gap-2 flex-1 max-w-xs">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-stone-400 text-sm"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Yer veya şehir ara..."
                className="bg-transparent text-sm text-stone-700 outline-none flex-1"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap flex-1">
              {regions.map((r) => (
                <button
                  key={r}
                  onClick={() => setSelectedRegion(r)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap
                    ${selectedRegion === r ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className="flex items-center bg-stone-100 rounded-full p-1 gap-1 flex-shrink-0">
              <button
                onClick={() => setViewMode("grid")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === "grid" ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"
                }`}
              >
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-list-check"></i></div>
                Liste
              </button>
              <button
                onClick={() => { setViewMode("map"); setMapPlace(filtered[0] || places[0]); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === "map" ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"
                }`}
              >
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-map-2-line"></i></div>
                Harita
              </button>
            </div>
          </div>

          {/* Row 2: Category (tag) filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-stone-400 font-medium whitespace-nowrap">Kategori:</span>
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => handleTagSelect(tag)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                  selectedTag === tag
                    ? "bg-stone-800 text-white border-stone-800"
                    : "bg-white text-stone-500 border-stone-200 hover:border-stone-400 hover:text-stone-700"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <p className="text-sm text-stone-400">
              {filtered.length} yer bulundu
              {selectedTag !== "Tümü" && (
                <span className="ml-2 text-amber-600 font-medium">— {selectedTag}</span>
              )}
              {totalPages > 1 && viewMode === "grid" && (
                <span className="ml-2 text-stone-300">— Sayfa {safePage} / {totalPages}</span>
              )}
            </p>
            {totalPages > 1 && viewMode === "grid" && (
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-semibold transition-colors cursor-pointer ${
                      p === safePage ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-stone-300 text-4xl"></i>
              </div>
              <p className="text-stone-400">Arama kriterlerinize uygun yer bulunamadı.</p>
              <button
                onClick={() => { setSelectedTag("Tümü"); setSelectedRegion("Tümü"); setSearchQuery(""); }}
                className="mt-4 text-sm text-amber-600 hover:underline cursor-pointer"
              >
                Filtreleri temizle
              </button>
            </div>
          ) : viewMode === "grid" ? (
            <>
              <div className="divide-y divide-stone-100">
                {paginatedPlaces.map((place) => (
                  <Link
                    key={place.id}
                    to={`/place/${place.id}`}
                    className="group flex items-center gap-5 py-4 hover:bg-stone-50 px-2 transition-colors"
                  >
                    <div className="relative w-20 h-20 flex-shrink-0 overflow-hidden rounded-lg">
                      <img
                        src={place.image}
                        alt={place.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs text-stone-400">{place.city}</span>
                        <span className="text-stone-200">·</span>
                        <span className="text-xs text-stone-400">{place.region}</span>
                        {place.featured && (
                          <span className="text-xs text-amber-600 font-medium">Öne Çıkan</span>
                        )}
                      </div>
                      <h3 className="font-bold text-stone-900 text-base group-hover:text-amber-700 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {place.name}
                      </h3>
                      <p className="text-stone-500 text-xs leading-relaxed line-clamp-1 mt-0.5">{place.shortDesc}</p>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <div className="flex items-center gap-1">
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                          <span className="text-xs font-semibold text-stone-700">{place.rating}</span>
                        </div>
                        {place.tags.slice(0, 3).map((tag) => (
                          <button
                            key={tag}
                            onClick={(e) => { e.preventDefault(); handleTagSelect(tag); }}
                            className={`text-xs cursor-pointer transition-colors ${
                              selectedTag === tag ? "text-amber-600 font-semibold" : "text-stone-400 hover:text-amber-500"
                            }`}
                          >
                            #{tag}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-stone-300 group-hover:text-amber-500 transition-colors">
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  </Link>
                ))}
              </div>
              <PaginationBar />
              {totalPages > 1 && (
                <p className="mt-4 text-center text-xs text-stone-300">
                  Toplam {filtered.length} yer — {totalPages} sayfa
                </p>
              )}
            </>
          ) : (
            /* MAP VIEW */
            <div className="flex flex-col lg:flex-row gap-5 h-auto lg:h-[680px]">
              <div className="w-full lg:w-80 flex-shrink-0 overflow-y-auto space-y-2 lg:pr-1" style={{ maxHeight: "680px" }}>
                {filtered.map((place) => (
                  <button
                    key={place.id}
                    onClick={() => setMapPlace(place)}
                    className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                      mapPlace.id === place.id ? "border-amber-400 bg-amber-50" : "border-stone-100 bg-white hover:border-stone-200 hover:bg-stone-50"
                    }`}
                  >
                    <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden">
                      <img src={place.image} alt={place.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-0.5">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-map-pin-fill text-amber-500 text-xs"></i>
                        </div>
                        <span className="text-xs text-stone-400 truncate">{place.city}</span>
                      </div>
                      <p className="font-semibold text-stone-900 text-sm truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {place.name}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-stone-400">{place.region}</span>
                        <div className="flex items-center gap-0.5">
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                          <span className="text-xs text-stone-500">{place.rating}</span>
                        </div>
                      </div>
                    </div>
                    {mapPlace.id === place.id && (
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-amber-500"></i>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex-1 flex flex-col gap-4 min-h-[400px] lg:min-h-0">
                <div className="bg-white border border-stone-100 rounded-2xl p-4 flex items-center gap-4">
                  <div className="w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden">
                    <img src={mapPlace.image} alt={mapPlace.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-stone-900 text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {mapPlace.name}
                      </h3>
                      {mapPlace.featured && (
                        <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-semibold">Öne Çıkan</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <div className="flex items-center gap-1 text-stone-500 text-xs">
                        <i className="ri-map-pin-line text-amber-500"></i>
                        <span>{mapPlace.city}, {mapPlace.region}</span>
                      </div>
                      <div className="flex items-center gap-1 text-stone-500 text-xs">
                        <i className="ri-star-fill text-amber-400"></i>
                        <span>{mapPlace.rating} / 5</span>
                      </div>
                      <div className="flex items-center gap-1 text-stone-500 text-xs">
                        <i className="ri-calendar-line text-amber-500"></i>
                        <span>{mapPlace.bestTime}</span>
                      </div>
                    </div>
                    <p className="text-stone-500 text-xs mt-1 line-clamp-1">{mapPlace.shortDesc}</p>
                  </div>
                  <Link
                    to={`/place/${mapPlace.id}`}
                    className="flex-shrink-0 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-4 py-2 rounded-full cursor-pointer transition-colors whitespace-nowrap"
                  >
                    Detay
                  </Link>
                </div>
                <div className="flex-1 rounded-2xl overflow-hidden border border-stone-100 min-h-[320px]">
                  <iframe
                    key={mapPlace.id}
                    title={`${mapPlace.name} Harita`}
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: "320px" }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={getMapUrl(mapPlace)}
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {mapPlace.highlights.map((h) => (
                    <span key={h} className="flex items-center gap-1 bg-stone-50 border border-stone-100 text-stone-600 text-xs px-3 py-1 rounded-full">
                      <i className="ri-checkbox-circle-line text-amber-500 text-xs"></i>
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PlacesPage;
